import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {BehaviorSubject, Observable, catchError, map, of, throwError} from 'rxjs';
import {SearchPaginatedAdxInvoiceResponse} from '../model/search-paginated-adx-invoice-response';
import {AdxInvoiceFilterResult} from '../model/adx-invoice-filter-result';
import {AdxInvoiceDetailResponse, SearchPaginatedAdxInvoiceDetailResponse} from '../model/adx-invoice-detail-response';
import {BaseHistoryModel} from 'src/app/adx-booking/model/base-history-model';
import {AdxInvoice} from '../model/adx-invoice';
import {CreateAdxInvoiceFromFormCommand} from '../model/create-adx-invoice-from-form-command';
import {IAdxErpReportItem} from '../../dashboard-finance/adx-erp-panel/interfaces/adx-erp-panel.interface';

@Injectable({
  providedIn: 'root'
})

export class AdxInvoiceService {
  private resourcePath: string = 'dbov2/adx-invoice';
  private _adxInvoiceFilterSubject: BehaviorSubject<AdxInvoiceFilterResult> = new BehaviorSubject<AdxInvoiceFilterResult>({});

  constructor(private httpClient: HttpClient) {
  }

  searchPaginatedAdxInvoices(adxInvoiceFilter: any): Observable<SearchPaginatedAdxInvoiceResponse> {
    let httpParams = new HttpParams();
    Object
      .keys(adxInvoiceFilter)
      .forEach((key) => {
        if (adxInvoiceFilter[key]) {
          httpParams = httpParams.append(key, adxInvoiceFilter[key]);
        }
      });
      return this.httpClient
        .get<SearchPaginatedAdxInvoiceResponse>(`${this.resourcePath}`, {params: httpParams})
        .pipe(catchError(this.errorHandler));
  }

  searchByQueryAdxInvoices(query: any): Observable<AdxInvoiceDetailResponse[]> {
    let httpParams = new HttpParams();
    Object
      .keys(query)
      .forEach((key) => {
        if (query[key]) {
          httpParams = httpParams.append(key, query[key]);
        }
      });

      var response =  this.httpClient
      .get<SearchPaginatedAdxInvoiceDetailResponse>(`${this.resourcePath}/search`, {params: httpParams})
      .pipe(
        catchError(this.errorHandler),
          map((response) => response?.items || [])
      );

      return response;
  }

  getAdxInvoiceById(id:string): Observable<AdxInvoice> {
    return this.httpClient.get<AdxInvoice>(`${this.resourcePath}/${id}`);
  }

  errorHandler(error: HttpErrorResponse) {
    return throwError(error.message || 'server Error');
  }

  getAdxInvoiceFilter(): Observable<AdxInvoiceFilterResult> {
    return this._adxInvoiceFilterSubject.asObservable();
  }

  setAdxInvoiceFilter(value: AdxInvoiceFilterResult): void {
    this._adxInvoiceFilterSubject.next(value);
  }

  getInvoiceHistoryById(id: number): Observable<[BaseHistoryModel[]]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('AdxInvoiceId', id);

    return this.httpClient
      .get<[BaseHistoryModel[]]>(`${this.resourcePath}/history`, {params: httpParams})
      .pipe(catchError(this.errorHandler));
  }

  createAdxInvoiceFromForm(command: CreateAdxInvoiceFromFormCommand): Observable<AdxInvoice> {
    return this.httpClient
      .post<AdxInvoice>(`${this.resourcePath}/create-from-form`, JSON.stringify(command))
      .pipe(catchError(this.errorHandler));
  }

  getErpPayload(adxInvoiceId: number): Observable<IAdxErpReportItem | null> {
    const httpParams = new HttpParams()
      .set('adxInvoiceId', adxInvoiceId)
      .set('pageSize', 1)
      .set('pageNumber', 1);

    return this.httpClient
      .get<{items: IAdxErpReportItem[]}>(`${this.resourcePath}/erp-report`, {params: httpParams})
      .pipe(
        map((response) => response?.items?.[0] ?? null),
        catchError(this.errorHandler)
      );
  }

  /**
   * Returns the raw ingested payload for an AdxInvoice, independent of whether the invoice has
   * been pushed through the ERP outbound pipeline (unlike getErpPayload/erp-report, which only
   * has data once an AdxInvoiceErpResponse row exists).
   *
   * Note: the API returns the payload as a JSON-encoded string (Content-Type: application/json),
   * i.e. the wire body is itself a quoted/escaped JSON string literal wrapping the raw payload
   * JSON text. Using the default 'json' responseType here (not 'text') lets HttpClient's
   * JSON.parse unwrap that one layer of encoding, giving back the original payload text.
   */
  getInvoicePayload(adxInvoiceId: number): Observable<string | null> {
    return this.httpClient
      .get<string>(`${this.resourcePath}/${adxInvoiceId}/payload`)
      .pipe(
        catchError(() => of(null))
      );
  }
}